import type { ItemInterface } from 'common/services/Interfaces';
declare global {
  interface Window {
    gtag: any;
    dataLayer: any;
  }
}

export const callGA4 = (eventName, data) => {
  if (eventName === 'begin_checkout') {
    beginCheckout(data);
  } else if (eventName === 'add_shipping_info') {
    addShippingInfo(data);
  } else if (eventName === 'add_payment_info') {
    addPaymentInfo(data);
  } else if (eventName === 'purchase') {
    gaPurchase(data);
  } else if (eventName === 'coupon_list_viewed') {
    couponListViewed(data);
  } else if (eventName === 'coupon_applied_success') {
    couponAppliedSuccess(data);
  } else if (eventName === 'coupon_applied_failed') {
    couponAppliedFailed(data);
  } else if (eventName.includes?.('_gk')) {
    additionalGaEvents(eventName, data);
  }
};

const beginCheckout = (data) => {
  const passAdditionalDataInGA4: boolean = 'additionalData' in data;
  const ecommerce = {
    value: parseFloat(data.totalValue),
    currency: 'INR',
    items: createItemsBody(data.cart, passAdditionalDataInGA4, data.additionalData?.website, data.discountAmount),
    coupon: data?.commonAnalyticsData?.coupon || 'NA',
  };
  let gkDataObj = {
    ...(!passAdditionalDataInGA4 && { payment_type: 'Pay Via UPI / COD' }),
    send_to: data.ga4Id,
    ...(passAdditionalDataInGA4 ? { ecommerce } : ecommerce),
  };
  if (passAdditionalDataInGA4) {
    gkDataObj = {
      ...gkDataObj,
      ...data.additionalData,
    };
  }
  triggerGAEvent('gokwik_checkout_initiated', gkDataObj);
  triggerGAEvent('begin_checkout', gkDataObj);
};

const addShippingInfo = (data) => {
  const passAdditionalDataInGA4: boolean = 'additionalData' in data;
  const ecommerce = {
    value: parseFloat(data.totalValue),
    currency: 'INR',
    items: createItemsBody(data.cart, passAdditionalDataInGA4, data.additionalData?.website, data.discountAmount),
    shipping_tier: data.shippingMethod,
    coupon: data.commonAnalyticsData?.coupon || 'NA',
  };
  let gkDataObj = {
    ...(passAdditionalDataInGA4 ? { ecommerce } : ecommerce),
    send_to: data.ga4Id,
  };
  if (passAdditionalDataInGA4) {
    gkDataObj = {
      ...gkDataObj,
      ...data.additionalData,
    };
  }
  triggerGAEvent('add_shipping_info', gkDataObj);
};

const addPaymentInfo = (data) => {
  const passAdditionalDataInGA4: boolean = 'additionalData' in data;
  const ecommerce = {
    value: parseFloat(data.totalValue),
    currency: 'INR',
    items: createItemsBody(data.cart, passAdditionalDataInGA4, data.additionalData?.website, data.discountAmount),
    payment_type: data.evt?.paymentMethod,
    coupon: data?.commonAnalyticsData?.coupon || 'NA',
  };
  let gkDataObj = {
    ...(!passAdditionalDataInGA4 && { payment_type: data.evt?.paymentMethod }),
    ...(passAdditionalDataInGA4 ? { ecommerce } : ecommerce),
    send_to: data.ga4Id,
  };
  if (passAdditionalDataInGA4) {
    gkDataObj = {
      ...gkDataObj,
      ...data.additionalData,
    };
  }
  triggerGAEvent('add_payment_info', gkDataObj);
};
const additionalGaEvents = (eventName, data) => {
  const passAdditionalDataInGA4: boolean = 'additionalData' in data;
  const ecommerce = {
    value: parseFloat(data.totalValue),
    currency: 'INR',
    items: createItemsBody(data.cart, passAdditionalDataInGA4, data.additionalData?.website, data.discountAmount),
    send_to: data.ga4Id,
  };
  triggerGAEvent(eventName, ecommerce);
};

const gaPurchase = (data) => {
  const passAdditionalDataInGA4: boolean = 'additionalData' in data;
  const totalValue = data.totalValue + data?.commonAnalyticsData?.codCharges
  const ecommerce = {
    value: data?.commonAnalyticsData?.codCharges
      ? parseFloat(totalValue)
      : parseFloat(data.totalValue),
    currency: 'INR',
    transaction_id: data.orderName,
    ...((data?.mid === 'eey3k3mkrg55tcs' || data?.mid === '12wyqc2lkv1ku8c17lg') && {
      order_name: data.orderName,
      moid: data.moid,
    }),
    tax: 0,
    shipping: data?.commonAnalyticsData?.codCharges
      ? data.shippingPrice + data?.commonAnalyticsData?.codCharges
      : data.shippingPrice,
    items: createItemsBody(data.cart, passAdditionalDataInGA4, data.additionalData?.website, data.discountAmount),
    coupon: data?.commonAnalyticsData?.coupon || 'NA',
    payment_type: data?.paymentMethod || 'NA',
  };
  let gkDataObj: any = {
    send_to: data.ga4Id,
    discount: data?.discountAmount || 0,
    shipping_name: data?.shippingMethod || '',
    ...(passAdditionalDataInGA4 && { payment_status: 'Success' }),
    ...(passAdditionalDataInGA4 ? { ecommerce } : ecommerce),
  };
  if (passAdditionalDataInGA4) {
    gkDataObj = {
      ...gkDataObj,
      ...data.additionalData,
    };
  }
  if (['3mt5u7utwrl303equb', 'eey3k3mkrg55tcs', 'eey3m1vkrm1rbo7'].includes(data?.mid)) {
    gkDataObj.userData = {
      first_name: data.userAddress?.firstname,
      last_name: data.userAddress?.lastname,
      phone: data.userAddress?.phone,
      email: data.userAddress?.email,
      city: data.userAddress?.city,
      state: data.userAddress?.state,
      address: data.userAddress?.address,
      country: data.userAddress?.country,
      pincode: data.userAddress?.pincode,
    };
  }
  triggerGAEvent('purchase', gkDataObj);
};

const couponListViewed = (data) => {
  let ecommerce = {
    coupons_available: data.coupons_available,
    coupons_unavailable: data.coupons_unavailable,
    best_coupon_available_name: data.best_coupon_available_name,
    best_coupon_savings: data.best_coupon_savings,
    cart_subtotal: data.cart_subtotal,
    checkout_step: data.checkout_step,
  };
  let gkDataObj = {
    send_to: data.ga4Id,
    ecommerce: ecommerce,
  };
  triggerGAEvent('coupon_list_viewed', gkDataObj);
};

const couponAppliedSuccess = (data) => {
  let ecommerce = {
    coupon_code: data.coupon_code,
    amount_saved: data.amount_saved,
    cart_subtotal: data.cart_subtotal,
    checkout_step: data.checkout_step,
  };
  let gkDataObj = {
    send_to: data.ga4Id,
    ecommerce: ecommerce,
  };
  triggerGAEvent('coupon_applied_success', gkDataObj);
};

const couponAppliedFailed = (data) => {
  let ecommerce = {
    coupon_code: data.coupon_code,
    cart_subtotal: data.cart_subtotal,
    checkout_step: data.checkout_step,
  };
  let gkDataObj = {
    send_to: data.ga4Id,
    ecommerce: ecommerce,
  };
  triggerGAEvent('coupon_applied_failed', gkDataObj);
};

const createItemsBody = (
  cart,
  passAdditionalDataInGA4: boolean = false,
  website?: string,
  totalDiscount: number = 0
) => {
  const items: ItemInterface[] = cart.items;
  return items.map((item, index) => {
    let itemEventPayload = {
      item_id: passAdditionalDataInGA4
        ? window.location.href.includes('snitch.co.in')
          ? item.id
          : item.sku
        : item.id || 'NA',
      item_name: item.product_title,
      item_variant: item.variant_title || 'NA',
      item_brand: item.vendor,
      item_category: '',
      price:
        isNaN(+item.final_line_price / 100) || item.is_prepaid_discounted_product || item.cart_verification_check
          ? 0
          : +item.final_line_price / 100,
      quantity: item.quantity,
      category: item.product_type,
      index: index,
    };
    if (passAdditionalDataInGA4) {
      const additionalData = {
        item_category2: item?.properties?._item_category2
          ? item?.properties?._item_category2
          : item?.item_category2 || 'NA',
        item_category3: item?.properties?._item_category3
          ? item?.properties?._item_category3
          : item?.item_category3 || 'NA',
        item_category4: item?.properties?._item_category4
          ? item?.properties?._item_category4
          : item?.item_category4 || 'NA',
        item_category5: item?.properties?._item_category5
          ? item?.properties?._item_category5
          : item?.item_category5 || 'NA',
        item_list_id: item?.properties?._item_list_id ? item?.properties?._item_list_id : item?.item_list_id || 'NA',
        item_list_name: item?.properties?._item_list_name
          ? item?.properties?._item_list_name
          : item?.item_list_name || 'NA',
        item_category: item?.properties?._item_category
          ? item?.properties?._item_category
          : item?.item_category || 'NA',
        item_size: item.options_with_values?.find?.((itm) => itm.name === 'Size')?.value || item.item_size || 'NA',
        item_delivery_days: item.item_delivery_days || 'NA',
        affiliation: 'Levis Website',
        coupon: item?.discounts[0]?.title || 'NA',
        discount: totalDiscount ? +totalDiscount / +cart.item_count : 0,
        item_brand: 'Levis',
        compare_at_price: item?.properties?._compare_at_price
          ? +item?.properties?._compare_at_price / 100
          : +(item?.compare_at_price || 0) / 100 || 'NA',
        location_id: 'ChIJIQBpAG2ahYAR_6128GcTUEo',
        sku: item.sku || 'NA',
      };
      itemEventPayload = {
        ...itemEventPayload,
        ...additionalData,
      };
    }
    return itemEventPayload;
  });
};

const createItemsBodyForSnitch = (inputObject, eventName) => {
  const additionalKeys = {
    add_shipping_info: { shipping_tier: inputObject?.ecommerce?.shipping_tier },
    add_payment_info: { payment_type: inputObject?.ecommerce?.payment_type },
    purchase: {
      tax: inputObject?.ecommerce?.tax,
      transaction_id: inputObject?.ecommerce?.moid,
      order_name: inputObject?.ecommerce?.order_name,
      shipping: inputObject?.ecommerce?.shipping,
      payment_type: inputObject?.ecommerce?.payment_type,
    },
    coupon_list_viewed: {
      coupons_available: inputObject.coupons_available,
      coupons_unavailable: inputObject.coupons_unavailable,
      best_coupon_available_name: inputObject.best_coupon_available_name,
      best_coupon_savings: inputObject.best_coupon_savings,
      cart_subtotal: inputObject.cart_subtotal,
      checkout_step: inputObject.checkout_step,
    },
    coupon_applied_success: {
      coupon_code: inputObject.coupon_code,
      amount_saved: inputObject.amount_saved,
      cart_subtotal: inputObject.cart_subtotal,
      checkout_step: inputObject.checkout_step,
    },
    coupon_applied_failed: {
      coupon_code: inputObject.coupon_code,
      cart_subtotal: inputObject.cart_subtotal,
      checkout_step: inputObject.checkout_step,
    },
  };
  const outPutObject = {
    ecommerce: {
      checkout_gateway: 'Gokwik',
      currency: inputObject.ecommerce.currency,
      value: inputObject.ecommerce.value,
      ...(additionalKeys[eventName] || {}),
      items: inputObject.ecommerce.items.map((item) => ({
        item_id: item.item_id,
        item_name: item.item_name,
        affiliation: 'SNITCH',
        coupon: item.coupon || 'NA',
        discount: item.discount || 0,
        index: item.index || 0,
        item_brand: 'SNITCH',
        item_category: item.item_category || 'NA',
        item_category2: item.item_category2 || 'NA',
        item_category3: item.item_category3 || 'NA',
        item_category4: item.item_category4 || 'NA',
        item_category5: item.item_category5 || 'NA',
        item_list_id: item.item_list_id || 'NA',
        item_list_name: item.item_list_name || 'NA',
        item_variant: item.item_variant || 'NA',
        item_size: item.item_size || 'NA',
        item_delivery_days: item.item_delivery_days || 'NA',
        sku: item.sku || 'NA',
        location_id: '',
        price: item.price || 0,
        quantity: item.quantity || 0,
      })),
    },
  };
  return outPutObject;
};

const triggerGAEvent = (eventName, eventData) => {
  if (window.location.href.includes('snitch.co.in')) {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: eventName, ...createItemsBodyForSnitch(eventData, eventName) });
  } else {
    window.gtag('event', eventName, eventData);
  }
};
